import { RecommendationId } from "@labs/shared/types/RecommendationId.ts";
import { Recommendation } from "@labs/shared/types/Recommendation.ts";
import { RecommendationType } from "@labs/shared/types/RecommendationType.ts";

export const recommendations: Record<RecommendationId, Recommendation> = {
  [RecommendationId.ElectricEfficiencyLow]: {
    id: RecommendationId.ElectricEfficiencyLow,
    title: "Electric Efficiency Is Below 20%",
    description:
      "More energy efficiency investment will reduce your clean power purchasing costs. We recommend a 25% energy intensity reduction target",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope2CutEnergyWaste",
    priority: 0,
  },
  [RecommendationId.ElectricEfficiencyHigh]: {
    id: RecommendationId.ElectricEfficiencyHigh,
    title: "Electric Efficiency Is Above 30%",
    description:
      "Your plan calls for higher than typical electric intensity reduction. Validate that your facilities have the economically viable potential to achieve these targets",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope2CutEnergyWaste",
    priority: 0,
  },
  [RecommendationId.GasEfficiencyHigh]: {
    id: RecommendationId.GasEfficiencyHigh,
    title: "Validate Gas Efficiency Targets",
    description:
      "Your plan calls for higher than typical gas intensity reduction. Validate that your facilities have the economically viable potential to achieve these targets",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope1CutEnergyWaste",
    priority: 0,
  },
  [RecommendationId.GasolineFleet]: {
    id: RecommendationId.GasolineFleet,
    title: "Electrify Gasoline Fleet",
    description:
      "Electrify part or all of your transportation fleet to reduce emissions from gasoline",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope1Electrify",
    priority: 0,
  },
  [RecommendationId.DieselFleet]: {
    id: RecommendationId.DieselFleet,
    title: "Electrify Diesel Fleet",
    description:
      "Electrify part or all of your transportation fleet to reduce emissions from diesel",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope1Electrify",
    priority: 0,
  },
  [RecommendationId.GasEfficiencyElectrification]: {
    id: RecommendationId.GasEfficiencyElectrification,
    title: "Electrify Low-Temperature Systems",
    description:
      "You're making a great investment in natural gas efficiency. Next, look at electrifying low-temperature systems",
    type: RecommendationType.EmissionToSolutionGap,
    href: "/plan/entry/scope1Electrify",
    priority: 0,
  },
  [RecommendationId.GasEfficiencyLow]: {
    id: RecommendationId.GasEfficiencyLow,
    title: "Increase Gas Efficiency",
    type: RecommendationType.EmissionToSolutionGap,
    description:
      "Investments in thermal system efficiency are often cost-effective and can reduce future electrification costs. We recommend a 15% energy intensity reduction target",
    href: "/plan/entry/scope1CutEnergyWaste",
    priority: 0,
  },
  [RecommendationId.HighElectrification]: {
    id: RecommendationId.HighElectrification,
    title: "Confirm Electrification Potential",
    description:
      "That's a lot of electrification! Confirm your facilities have enough economically viable potential",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope1Electrify",
    priority: 0,
  },
  [RecommendationId.Scope1Low]: {
    id: RecommendationId.Scope1Low,
    title: "Increase Scope 1 Solutions",
    description:
      "Your plan is light on Scope 1 solutions. Reassess if you have opportunities to cut waste, electrify or buy clean fuels",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan",
    priority: 0,
  },
  [RecommendationId.Scope2Low]: {
    id: RecommendationId.Scope2Low,
    title: "Increase Scope 2 Solutions",
    description:
      "Your plan is light on Scope 2 solutions. Reassess if you have opportunities to cut waste or buy clean",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan",
    priority: 0,
  },
  [RecommendationId.HighSolar]: {
    id: RecommendationId.HighSolar,
    title: "Pair Solar with Efficiency",
    description:
      "Consider pairing onsite generation with energy efficiency investments. Did you know that eliminating the need for a 10hp pump has the same decarbonization impact as 2,000 sq ft of solar panels?",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan/entry/scope2BuyClean",
    priority: 0,
  },
  [RecommendationId.LowTarget]: {
    id: RecommendationId.LowTarget,
    title: "Increase Target Ambition",
    description:
      "Your target is low and may not qualify as a Science Based Target. We recommend shooting for 40% reduction by 2030",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/target",
    priority: 0,
  },
  [RecommendationId.EarlyTarget]: {
    id: RecommendationId.EarlyTarget,
    title: "Assess Target Feasibility",
    description:
      "Your target year is soon. Are you sure you will be able to achieve your goal in that time?",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/target",
    priority: 0,
  },
  [RecommendationId.UnaddressedEmissions]: {
    id: RecommendationId.UnaddressedEmissions,
    title: "Address Unaddressed Emissions",
    description:
      "You have emissions that are unaddressed. We recommend starting with efficiency and then moving to electrification",
    type: RecommendationType.EmissionToSolutionGap,
    href: "/plan",
    priority: 0,
  },
  [RecommendationId.HighOffsets]: {
    id: RecommendationId.HighOffsets,
    title: "Verify Offsets Eligibility",
    description:
      "Verify that your planned offsets are eligible for inclusion, as many reporting protocols do not allow offsets for scope 1 or 2 reduction. Offsets may be cheaper short-term, but often have worse ROI than behind-the-meter solutions like efficiency and electrification",
    type: RecommendationType.IneffectiveSolutions,
    href: "/plan/entry/scope1LessCommonSolutions",
    priority: 0,
  },
  [RecommendationId.CarbonCaptureIneffective]: {
    id: RecommendationId.CarbonCaptureIneffective,
    title: "Assess Carbon Capture Feasibility",
    description:
      "Carbon Capture technology is in early stages of development and is unlikely to be cost-effective by your target year",
    type: RecommendationType.IneffectiveSolutions,
    href: "/plan/entry/scope1LessCommonSolutions",
    priority: 0,
  },
  [RecommendationId.HighRECs]: {
    id: RecommendationId.HighRECs,
    title: "Invest in Energy Efficiency",
    description:
      "Buying RECs is a common and effective Scope 2 solution, but we also recommend more investment in energy efficiency projects to reduce your ongoing REC spend",
    type: RecommendationType.IneffectiveSolutions,
    href: "/plan/entry/scope2BuyClean",
    priority: 0,
  },
  [RecommendationId.HighPPA]: {
    id: RecommendationId.HighPPA,
    title: "Invest in Energy Efficiency",
    description:
      "The value of PPA and VPPA contracts can fluctuate widely. We recommend more investment in energy efficiency projects to reduce your PPA spend",
    type: RecommendationType.IneffectiveSolutions,
    href: "/plan/entry/scope2BuyClean",
    priority: 0,
  },
  [RecommendationId.PlanLessThanTarget]: {
    id: RecommendationId.PlanLessThanTarget,
    title: "Add More Solutions",
    description: "Your plan does not achieve your target. Add more solutions",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan",
    priority: 0,
  },
  [RecommendationId.PlanGreaterThanTarget]: {
    id: RecommendationId.PlanGreaterThanTarget,
    title: "Optimize Plan Costs",
    description:
      "Your plan exceeds your target - congratulations! Lower the cost of your plan by reducing solutions with the highest investment",
    type: RecommendationType.UnderOrOverPlanning,
    href: "/plan",
    priority: 0,
  },
};
